import { BANNER_CTA_BUTTON_SX } from "components/Banner/constants";
import { EVOTRUX_ERROR_RED, SUPPORT_EMAIL } from "constants/index";

import type { SxProps, Theme } from "@mui/material";

// How a lapsed subscription is recovered. The reason decides which of these the
// banner offers, and the plan can downgrade BILLING_PORTAL to SUPPORT_EMAIL when
// the company has no Stripe customer to open a portal session against.
export type RecoveryAction =
  "BILLING_PORTAL" | "UPGRADE_PAGE" | "SUPPORT_EMAIL";

// The reasons that render a banner. NONE is the active sentinel, and SUPERSEDED
// means a NEWER ACTIVE subscription exists, so rendering either would alarm a
// customer who is not lapsed at all.
export type LapsedSubscriptionReason = Exclude<
  SubscriptionInactivityReason,
  "NONE" | "SUPERSEDED"
>;

interface LapsedSubscriptionCopy {
  headline: string;
  body: string;
  primaryLabel: string;
  primaryAction: RecoveryAction;
  // Omitted when the primary action already is the support mailto, so the
  // banner never offers the same action twice.
  secondaryLabel?: string;
}

export const LAPSED_SUBSCRIPTION_COPY: Record<
  LapsedSubscriptionReason,
  LapsedSubscriptionCopy
> = {
  PAYMENT_FAILED: {
    headline: "Your subscription payment failed",
    body: "Update your payment method to keep using Evotrux.",
    primaryLabel: "Update payment method",
    primaryAction: "BILLING_PORTAL",
    secondaryLabel: "Contact support",
  },
  CANCELLED: {
    headline: "Your subscription is cancelled",
    body: "Reactivate your plan to keep using Evotrux.",
    primaryLabel: "Reactivate subscription",
    primaryAction: "UPGRADE_PAGE",
    secondaryLabel: "Contact support",
  },
  PAUSED: {
    headline: "Your subscription is paused",
    body: "Contact support to resume your account.",
    primaryLabel: "Email support",
    primaryAction: "SUPPORT_EMAIL",
  },
  UNKNOWN: {
    headline: "Your subscription is inactive",
    body: "Choose a plan to keep using Evotrux.",
    primaryLabel: "View plans",
    primaryAction: "UPGRADE_PAGE",
    secondaryLabel: "Contact support",
  },
};

export const RECOVERY_ACTION_TOOLTIP: Record<RecoveryAction, string> = {
  BILLING_PORTAL:
    "Opens the Stripe billing portal, where you can update the card we charge.",
  UPGRADE_PAGE: "Opens the plans page so you can pick a subscription.",
  SUPPORT_EMAIL: `Opens your email client with a message to ${SUPPORT_EMAIL}.`,
};

export const LAPSED_SUBSCRIPTION_SUPPORT_SUBJECT = "Subscription help";

// Used when a reason's primary action falls back to the support mailto, so the
// button never promises something it cannot do (a "Update payment method"
// button that opens an email draft is worse than no button).
export const SUPPORT_FALLBACK_LABEL = "Email support";

// The white call-to-action shared with the app-bar banner, recoloured for the
// red bar. Module scope so the object identity is stable across renders.
export const PRIMARY_BUTTON_SX: SxProps<Theme> = {
  ...BANNER_CTA_BUTTON_SX,
  color: EVOTRUX_ERROR_RED,
  flexShrink: 0,
};

// A user who reloads the app keeps seeing the banner, so capturing on render
// would fire the shown event once per page load and distort the funnel
// denominator. One entry per company per session instead, holding the reason its
// banner was last shown for.
export const SHOWN_EVENT_SESSION_KEY_PREFIX =
  "lapsed-subscription-banner-shown";
